import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { LijnGrafiek } from '../components/charts';
import {
  Avatar,
  Badge,
  Cel,
  Kaart,
  KaartKop,
  Leeg,
  PaginaKop,
  RangBadge,
  Schakelaar,
  Rij,
  Skelet,
  SkeletTegels,
  SkeletVlak,
  SpelerCel,
  Tabel,
  Tegel,
  TierBadge,
  VormReeks,
  WedstrijdRegel
} from '../components/ui';
import { Symbool } from '../components/icons';
import { SERIES } from '../lib/colors';
import {
  START_RATING,
  bestePositie,
  chronologisch,
  onderling,
  plekVan,
  positieStats,
  positieVerschil,
  potjesVan,
  reeksVan,
  relatiesVan,
  spelerStats
} from '../lib/elo';
import { toonNaam, toonRating } from '../lib/format';
import { TIERS, familieVerdeling, prestaties, rangBanden, rangVoortgang, vorm } from '../lib/prestaties';
import { useAuth } from '../lib/auth';
import { useLeague } from '../lib/queries';
import { usePoule } from '../lib/poule';

export default function SpelerProfiel() {
  const { pouleId } = usePoule();
  const { profile } = useAuth();
  /* Zonder :id in de route kijk je naar je eigen profiel — dat is /mijn-profiel. */
  const { id: uitPad } = useParams();
  const id = uitPad ?? profile?.id ?? '';
  const { players, matches, elo, ranking, zoekSpeler, rating, laden } = useLeague(pouleId);
  /* De rangverdeling mag ook over alle poules heen; dan tellen alle potjes mee. */
  const [bereik, setBereik] = useState<'poule' | 'alles'>('poule');
  const overal = useLeague(null);

  /* Zonder dit zou de pagina tijdens het laden "deze speler bestaat niet"
     roepen, simpelweg omdat de lijst nog leeg is. */
  if (laden) {
    return (
      <>
        <Kaart>
          <div className="flex items-center gap-4">
            <Skelet className="h-16 w-16 rounded-full" />
            <div>
              <Skelet className="h-6 w-40" />
              <Skelet className="mt-2 h-3 w-56" />
            </div>
          </div>
        </Kaart>
        <div className="mt-3.5">
          <SkeletTegels />
        </div>
        <div className="mt-3.5">
          <Kaart>
            <KaartKop titel="Rating in de tijd" />
            <SkeletVlak hoogte="h-52" />
          </Kaart>
        </div>
      </>
    );
  }

  const speler = players.find((p) => p.id === id);
  if (!speler) {
    return (
      <>
        <PaginaKop titel="Speler" />
        <Kaart>
          <Leeg tekst="Deze speler bestaat niet (meer)." ikoon="geest" />
        </Kaart>
      </>
    );
  }

  const plek = plekVan(speler.id, ranking);
  const s = spelerStats(speler.id, matches);
  const reeks = reeksVan(speler.id, matches);
  const rel = relatiesVan(speler.id, matches);
  const verloop = elo.verloop[speler.id] ?? [];
  const eigenPotjes = chronologisch(potjesVan(speler.id, matches)).reverse();
  const voortgang = rangVoortgang(rating(speler.id));
  const lijst = prestaties(speler.id, matches, verloop);
  const behaald = lijst.filter((p) => p.behaald);

  const bron = bereik === 'poule' ? { spelers: players, elo, potjes: matches } : { spelers: overal.players, elo: overal.elo, potjes: overal.matches };
  const verdeling = familieVerdeling(
    bron.spelers.filter((p) => p.isActive),
    (pid) => bron.elo.ratings[pid] ?? START_RATING,
    (pid) => potjesVan(pid, bron.potjes).length,
    speler.id
  );

  /* Achterin of voorin: de opstelling zit in de volgorde van het team. */
  const posities = positieStats(speler.id, matches);
  const sterkstePositie = bestePositie(posities);
  const positieKloof = positieVerschil(posities);
  const duoPotjes = posities.verdediger.gespeeld + posities.aanvaller.gespeeld;

  const punten = [
    { x: verloop.length ? new Date(verloop[0].at).getTime() - 86400000 : Date.now(), y: START_RATING },
    ...verloop.map((v) => ({ x: new Date(v.at).getTime(), y: v.rating }))
  ];

  const tegenstanders = rel.tegenIds
    .map((tid) => ({ id: tid, ...onderling(speler.id, tid, matches) }))
    .filter((r) => r.gespeeld > 0)
    .sort((a, b) => b.gespeeld - a.gespeeld);

  return (
    <>
      <Kaart>
        <div className="flex flex-wrap items-center gap-4">
          <Avatar speler={speler} maat="lg" />
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight">{toonNaam(speler)}</h1>
            {speler.bijnaam ? (
              <p className="mt-0.5 text-sm text-krijt-600">
                &ldquo;<span className="font-medium">{speler.bijnaam}</span>&rdquo;
                {speler.bijnaamDoor ? (
                  <span className="text-krijt-500"> — zo genoemd door {toonNaam(zoekSpeler(speler.bijnaamDoor))}</span>
                ) : null}
              </p>
            ) : null}
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-krijt-600">
              {speler.isActive === false ? <Badge toon="uit">gestopt</Badge> : null}
              <RangBadge rating={rating(speler.id)} uitleg />
              {plek ? <span>{plek}e op de ranglijst ·</span> : null}
              <span>{toonRating(rating(speler.id))} punten</span>
            </div>
          </div>

          <div className="ml-auto flex items-center gap-4">
            <div>
              <div className="opschrift mb-1 text-krijt-500">Vorm</div>
              <VormReeks potjes={vorm(speler.id, matches)} />
            </div>
          </div>
        </div>

        {/* Hoe ver ben je in je rang, en wat kost de volgende? */}
        <div className="mt-4">
          <div className="flex items-baseline justify-between text-xs text-krijt-500">
            <span>{voortgang.rang.label}</span>
            <span>
              {voortgang.volgende
                ? `nog ${voortgang.tekort} ${voortgang.tekort === 1 ? 'punt' : 'punten'} tot ${voortgang.volgende.label}`
                : 'hoogste rang bereikt'}
            </span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-krijt-200">
            <div
              className="h-full"
              style={{ width: `${Math.round(voortgang.deel * 100)}%`, background: voortgang.rang.kleur }}
            />
          </div>
        </div>
      </Kaart>

      <div className="mt-3.5 grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
        <Tegel label="Potjes" waarde={s.gespeeld} onder={`${s.gewonnen} gewonnen, ${s.verloren} verloren`} />
        <Tegel
          label="Winstpercentage"
          waarde={`${Math.round(s.winstPct)}%`}
          onder={s.gespeeld ? `over ${s.gespeeld} potjes` : ''}
        />
        <Tegel
          label="Doelsaldo"
          waarde={`${s.saldo > 0 ? '+' : ''}${s.saldo}`}
          onder={`${s.voor} voor, ${s.tegen} tegen`}
        />
        <Tegel
          label="Reeks"
          waarde={reeks.aantal ? `${reeks.aantal}×` : '—'}
          onder={reeks.aantal ? (reeks.soort === 'winst' ? 'gewonnen op rij' : 'verloren op rij') : 'nog niet gespeeld'}
        />
      </div>

      <div className="mt-3.5">
        <Kaart>
          <KaartKop titel="Rating in de tijd" hint="elk punt is een potje" />
          {punten.length > 1 ? (
            <LijnGrafiek
              reeksen={[{ naam: toonNaam(speler), kleur: SERIES[0], punten }]}
              hoogte={220}
              basis={START_RATING}
              banden={rangBanden()}
            />
          ) : (
            <Leeg tekst="Nog geen potjes gespeeld." ikoon="stijging" />
          )}
        </Kaart>
      </div>

      <div className="mt-3.5 grid gap-3.5 lg:grid-cols-2">
        <Kaart>
          <KaartKop titel="Met en tegen" hint="vanaf drie gezamenlijke potjes" />
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <div className="text-xs uppercase tracking-wide text-krijt-500">Beste ploeggenoot</div>
              {rel.ploeggenoot ? (
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <SpelerCel speler={zoekSpeler(rel.ploeggenoot.id)} />
                  <span className="text-xs text-krijt-500">
                    {Math.round(rel.ploeggenoot.pct)}% winst in {rel.ploeggenoot.gespeeld} potjes
                  </span>
                </div>
              ) : (
                <div className="mt-1.5 text-xs text-krijt-500">nog te weinig 2v2 gespeeld</div>
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-krijt-500">Lastigste tegenstander</div>
              {rel.boeman ? (
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <SpelerCel speler={zoekSpeler(rel.boeman.id)} />
                  <span className="text-xs text-krijt-500">
                    {Math.round(rel.boeman.pct)}% winst in {rel.boeman.gespeeld} potjes
                  </span>
                </div>
              ) : (
                <div className="mt-1.5 text-xs text-krijt-500">nog te weinig gespeeld</div>
              )}
            </div>
          </div>
        </Kaart>

        <Kaart>
          <KaartKop titel="Onderling" />
          {tegenstanders.length ? (
            <Tabel koppen={['Tegenstander', 'Potjes', 'Gewonnen', 'Verloren', 'Winst']}>
              {tegenstanders.map((r) => (
                <Rij key={r.id}>
                  <Cel>
                    <SpelerCel speler={zoekSpeler(r.id)} />
                  </Cel>
                  <Cel nummer>{r.gespeeld}</Cel>
                  <Cel nummer>{r.gewonnenA}</Cel>
                  <Cel nummer>{r.gespeeld - r.gewonnenA}</Cel>
                  <Cel nummer>{Math.round((r.gewonnenA / r.gespeeld) * 100)}%</Cel>
                </Rij>
              ))}
            </Tabel>
          ) : (
            <Leeg tekst="Nog niemand tegengekomen." ikoon="handdruk" />
          )}
        </Kaart>
      </div>

      <div className="mt-3.5">
        <Kaart>
          <KaartKop
            titel="Prestaties"
            hint={
              <>
                {behaald.length} van de {lijst.length} aangebroken · elke prestatie heeft zeven treden
              </>
            }
          />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {lijst.map((p) => (
              <div
                key={p.sleutel}
                className={`flex flex-col rounded-kaart border px-3.5 py-3 ${
                  p.tier ? 'border-krijt-300 bg-white' : 'border-krijt-200 bg-krijt-50'
                }`}
                style={p.tier ? { borderColor: `${p.tier.kleur}66` } : undefined}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className={`flex items-center gap-2 ${p.tier ? '' : 'opacity-40 grayscale'}`}>
                    <Symbool naam={p.ikoon} className="!h-5 !w-5" style={p.tier ? { color: p.tier.kleur } : undefined} />
                    <span className="text-sm font-semibold">{p.label}</span>
                  </span>
                  {p.tier ? <TierBadge tier={p.tier} klein /> : null}
                </div>

                <p className="mt-1.5 text-xs leading-snug text-krijt-600">{p.uitleg}</p>

                {/*
                  Eén balk naar het eerstvolgende doel. De kleur is die van de
                  trede die je hebt; heb je er nog geen, dan die van de trede
                  waar je naartoe werkt — zo is er altijd kleur en past hij bij
                  het plaatje ernaast.
                */}
                <div
                  className="mt-2 h-1.5 overflow-hidden rounded-full bg-krijt-200"
                  title={
                    p.volgende
                      ? `${p.stand} van de ${p.volgende.doel} voor ${p.volgende.tier.label}`
                      : 'alle treden binnen'
                  }
                >
                  <span
                    className="block h-full rounded-full transition-[width]"
                    style={{
                      width: `${Math.round(p.balk * 100)}%`,
                      background: (p.tier ?? p.volgende?.tier)?.kleur ?? 'var(--color-krijt-400)'
                    }}
                  />
                </div>


                <p className="nums mt-1.5 text-xs text-krijt-500">
                  {p.volgende ? (
                    <>
                      {p.stand} / {p.volgende.doel} {p.eenheid} → {p.volgende.tier.label}
                    </>
                  ) : (
                    <span className="font-semibold" style={{ color: p.tier?.kleur }}>
                      {p.stand} {p.eenheid} — alles binnen
                    </span>
                  )}
                </p>

                {/* De zeven treden onderaan: wat je hebt, en hoeveel er nog boven je ligt. */}
                <div className="mt-auto flex gap-[3px] pt-2.5">
                  {p.doelen.map((doel, i) => (
                    <span
                      key={doel}
                      title={`${TIERS[i].label} bij ${doel}`}
                      className="h-1.5 flex-1 rounded-full"
                      style={{ background: i <= p.tierIndex ? TIERS[i].kleur : 'var(--color-krijt-200)' }}
                    />
                  ))}
                </div>

              </div>
            ))}
          </div>
        </Kaart>
      </div>

      <div className="mt-3.5">
        <Kaart>
          <KaartKop
            titel="Waar staat deze rating"
            hint={
              <Schakelaar<'poule' | 'alles'>
                waarde={bereik}
                onKies={setBereik}
                opties={[
                  { waarde: 'poule', label: 'Deze poule' },
                  { waarde: 'alles', label: 'Alle poules' }
                ]}
              />
            }
          />

          {verdeling.totaal ? (
            <>
              <div className="flex items-end gap-2">
                {verdeling.vakken.map((v) => (
                  <div key={v.naam} className="flex-1 text-center">
                    <div className="flex h-28 items-end justify-center">
                      <div
                        className={`w-full rounded-t-sm ${v.jij ? 'ring-2 ring-krijt-900 ring-offset-1' : ''}`}
                        style={{
                          height: `${Math.max(4, v.deel * 100)}%`,
                          background: v.kleur,
                          opacity: v.jij ? 1 : 0.55
                        }}
                        title={`${v.aantal} van de ${verdeling.totaal} spelers`}
                      />
                    </div>
                    <div className="kop nums mt-1.5 text-[15px] leading-none">{v.aantal}</div>
                    <div className="opschrift text-krijt-500">{v.naam}</div>
                    {v.jij ? <div className="mt-0.5 text-[11px] font-semibold text-krijt-900">← hier</div> : null}
                  </div>
                ))}
              </div>

              <p className="mt-3 text-sm text-krijt-600">
                {verdeling.mijnRang ? (
                  <>
                    <strong>{toonNaam(speler)}</strong> staat in <strong>{verdeling.mijnRang.label}</strong>, op{' '}
                    {verdeling.mijnPlekGedeeld ? 'gedeelde plek' : 'plek'}{' '}
                    <span className="nums">{verdeling.mijnPlek}</span> van {verdeling.totaal}{' '}
                    {bereik === 'poule' ? 'spelers in deze poule' : 'spelers over alle poules'}.
                  </>
                ) : (
                  'Deze speler heeft hier nog niet gespeeld.'
                )}
              </p>
            </>
          ) : (
            <Leeg tekst="Nog niemand met een gespeeld potje." ikoon="statistiek" />
          )}
          {/* De volledige uitleg staat op /rangen; twee keer onderhouden gaat mis. */}
          <p className="mt-4 border-t border-krijt-200 pt-3 text-sm text-krijt-600">
            {voortgang.volgende ? (
              <>
                Nog <strong className="nums">{voortgang.tekort}</strong>{' '}
                {voortgang.tekort === 1 ? 'punt' : 'punten'} tot <strong>{voortgang.volgende.label}</strong>.{' '}
              </>
            ) : (
              <>Hoogste divisie bereikt.{' '}</>
            )}
            <Link to="/rangen" className="font-semibold text-veld-700 hover:underline">
              Hoe de rangen werken →
            </Link>
          </p>
        </Kaart>
      </div>

      <div className="mt-3.5">
        <Kaart>
          <KaartKop
            titel="Verdedigen of aanvallen"
            hint={<span className="text-xs text-krijt-500">alleen 2 tegen 2</span>}
          />
          {duoPotjes ? (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                {(['verdediger', 'aanvaller'] as const).map((pos) => {
                  const s = posities[pos];
                  return (
                    <div
                      key={pos}
                      className={`rand-hard p-4 ${sterkstePositie === pos ? 'ring-2 ring-veld-600' : ''}`}
                    >
                      <div className="opschrift text-krijt-500">
                        {pos === 'verdediger' ? 'Achterin' : 'Voorin'}
                      </div>
                      <div className="kop nums mt-0.5 text-[26px] leading-none">
                        {s.gespeeld ? `${s.winstPct}%` : '—'}
                      </div>
                      <div className="mt-1 text-sm text-krijt-600">
                        {s.gespeeld
                          ? `${s.gewonnen}–${s.verloren} uit ${s.gespeeld} ${s.gespeeld === 1 ? 'potje' : 'potjes'}`
                          : 'nog niet op deze plek gespeeld'}
                      </div>
                      {s.gespeeld ? (
                        <dl className="mt-2.5 grid grid-cols-3 gap-2 border-t border-krijt-200 pt-2.5 text-center">
                          <div>
                            <dt className="opschrift text-krijt-500">voor</dt>
                            <dd className="nums text-sm font-semibold">{s.voorGem}</dd>
                          </div>
                          <div>
                            <dt className="opschrift text-krijt-500">tegen</dt>
                            <dd className="nums text-sm font-semibold">{s.tegenGem}</dd>
                          </div>
                          <div>
                            <dt className="opschrift text-krijt-500">nul gehouden</dt>
                            <dd className="nums text-sm font-semibold">{s.nulAchter}</dd>
                          </div>
                        </dl>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              {/* Bij te weinig potjes staat er niets: de cijfers erboven spreken dan
                  voor zich, en een regel die zegt dat er niets te zeggen valt is ruis. */}
              {positieKloof === null ? null : (
                <p className="mt-3 text-sm text-krijt-600">
                  {sterkstePositie ? (
                    <>
                      Wint het vaakst <strong>{sterkstePositie === 'verdediger' ? 'achterin' : 'voorin'}</strong>
                      {Math.abs(positieKloof) >= 15 ? ' — en dat scheelt flink' : ''}:{' '}
                      <span className="nums">{Math.abs(positieKloof)}</span> procentpunt verschil. Gemiddeld
                      per potje krijgt deze speler achterin{' '}
                      <span className="nums">{posities.verdediger.tegenGem}</span> tegen en voorin{' '}
                      <span className="nums">{posities.aanvaller.tegenGem}</span>.
                    </>
                  ) : (
                    'Achterin en voorin leveren precies evenveel op — een echte alleskunner.'
                  )}
                </p>
              )}
            </>
          ) : (
            <Leeg tekst="Nog geen 2 tegen 2 gespeeld." ikoon="statistiek" />
          )}
        </Kaart>
      </div>

      <div className="mt-3.5">
        <Kaart>
          <KaartKop titel="Laatste potjes" />
          {eigenPotjes.length ? (
            eigenPotjes
              .slice(0, 10)
              .map((m) => (
                <WedstrijdRegel key={m.id} potje={m} zoekSpeler={zoekSpeler} delta={elo.perPotje[m.id]?.deltaA} deltaB={elo.perPotje[m.id]?.deltaB} />
              ))
          ) : (
            <Leeg tekst="Nog geen potjes." ikoon="bal" />
          )}
        </Kaart>
      </div>
    </>
  );
}
